import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

import type { AnimationSettingsLegacy } from '@sites/entity-core';
import { createAnimationLegacy } from '@sites/entity-core';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { SpSwitchComponent } from '../../ui/sp-switch/src/sp-switch.component';
import {
    EAnimationPosition,
    EAnimationSpeed,
    EAnimationType,
} from '../sp-animation-settings/type/animation-settings.type';

/**
 * Signal-form editor for the widget animation settings — the signal-form
 * analogue of the FormGroup-based `sp-animation-settings` panel. Mirrors that
 * template (on-scroll switch + type / position / speed selects, the
 * running-stripe alert and its left/right-only position restriction) but
 * drives state through Angular signals; the value shape is the canonical
 * `AnimationSettingsLegacy` from `@sites/entity-core`, i.e. exactly what a widget
 * signal form's `animation` branch holds.
 *
 * Contract (same as `sp-extended-position-signal-settings`):
 *
 *  - Parent binds a snapshot of its signal-form animation value via the
 *    `animation` input; changes flow in and resync the local model.
 *  - Edits update the local model and emit the next `AnimationSettingsLegacy`
 *    via `valueChangeEvent`; the parent calls `form.setAnimation(value)` so
 *    the signal form (and history dispatch) stays the single source of truth.
 */
@Component({
    selector: 'sp-animation-settings-signal-legacy',
    templateUrl: './sp-animation-settings-signal-legacy.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SpSwitchComponent, TranslateModule, TooltipModule],
})
export class SpAnimationSettingsSignalLegacyComponent {
    public readonly animation = input<AnimationSettingsLegacy | null>(null);

    public readonly showRunningStripe = input<boolean>(false);
    public readonly isDisabled = input<boolean>(false);

    public readonly valueChangeEvent = output<AnimationSettingsLegacy>();

    public readonly EAnimationType = EAnimationType;
    public readonly EAnimationPosition = EAnimationPosition;
    public readonly EAnimationSpeed = EAnimationSpeed;

    private readonly model = signal<AnimationSettingsLegacy>(createAnimationLegacy());

    public readonly isActive = computed(() => !this.isDisabled() && Boolean(this.model().isActive));
    public readonly animationType = computed(() => this.model().animationType);
    public readonly animationPosition = computed(() => this.model().animationPosition);
    public readonly animationSpeed = computed(() => this.model().animationSpeed);
    public readonly isRunningStripe = computed(() => this.animationType() === EAnimationType.runningStripe);

    constructor() {
        effect(() => {
            const incoming = this.animation();
            if (!incoming) return;
            this.model.set(incoming);
        });
    }

    // --- Setters (template event handlers) ---

    public toggleActive(value: boolean): void {
        if (this.isDisabled()) return;
        this.patch({ isActive: value });
    }

    public onTypeChange(event: Event): void {
        const value = (event.target as HTMLSelectElement).value as AnimationSettingsLegacy['animationType'];

        // Running stripe only animates horizontally — jump the position to
        // `right`, mirroring the FormGroup component's `setAnimation` hook.
        if (value === EAnimationType.runningStripe) {
            this.patch({ animationType: value, animationPosition: EAnimationPosition.right });
            return;
        }

        this.patch({ animationType: value });
    }

    public onPositionChange(event: Event): void {
        const value = (event.target as HTMLSelectElement).value as AnimationSettingsLegacy['animationPosition'];
        this.patch({ animationPosition: value });
    }

    public onSpeedChange(event: Event): void {
        const value = (event.target as HTMLSelectElement).value as AnimationSettingsLegacy['animationSpeed'];
        this.patch({ animationSpeed: value });
    }

    private patch(patch: Partial<AnimationSettingsLegacy>): void {
        this.model.update((m) => ({ ...m, ...patch }));
        this.valueChangeEvent.emit(this.model());
    }
}
